import { unreachable } from '../../utils/Utils';
import SB from '../../SchemaBuilder';
import type { DiscordClient } from '../Discord';
import { APIAccessInterface } from '../ServerReplicant';

// https://discord.com/developers/docs/events/webhook-events#application-authorized
const applicationAuthorizedEventSchema = SB.object({
    /** APPLICATION_AUTHORIZED is sent when the app is added to a server or user account. */
    type: SB.tuple(['APPLICATION_AUTHORIZED' as const]),

    data: SB.object({
        /**
         * Installation context for the authorization. Either guild (0) if installed to a server or
         * user (1) if installed to a user's account.
         */
        integration_type: SB.number(),

        /** User who authorized the app. */
        user: SB.object({
            id: SB.string(),
        }).additionalProperties(),

        /** List of scopes the user authorized. */
        scopes: SB.array(SB.string()),

        /** Server which app was authorized for (when integration type is 0). */
        guild: SB.object({
            id: SB.string(),
        })
            .additionalProperties()
            .optional(),
    }).additionalProperties(),
}).additionalProperties();

// https://discord.com/developers/docs/events/webhook-events#entitlement-create
const entitlementCreateEventSchema = SB.object({
    /** ENTITLEMENT_CREATE is sent when an entitlement is created when a user purchases or is otherwise granted one of your app's SKUs. */
    type: SB.tuple(['ENTITLEMENT_CREATE' as const]),

    data: SB.object({
        /** ID of the entitlement. */
        id: SB.string(),

        /** ID of the SKU. */
        sku_id: SB.string(),

        /** ID of the user that is granted access to the entitlement's sku. */
        user_id: SB.string(),
    }).additionalProperties(),
}).additionalProperties();

// All supported events
const eventSchema = SB.union([applicationAuthorizedEventSchema, entitlementCreateEventSchema]);

enum WebhookType {
    Ping = 0,
    Event = 1,
}

const bodySchema = SB.object({
    /** Type of webhook, either 0 for PING or 1 for webhook events. */
    type: SB.int(),

    /** Event data payload. */
    event: eventSchema.optional(),
}).additionalProperties();

export type DiscordWebhookHandler = {
    handleEvent(eventRequest: { body: unknown }): Promise<void>;
};

// https://discord.com/developers/docs/events/webhook-events
export function createDiscordWebhookHandler(opts: {
    apiAccess: APIAccessInterface<any>;
    discordClient: DiscordClient;
}): DiscordWebhookHandler {
    return {
        async handleEvent({ body }) {
            if (!bodySchema.isValid(body)) {
                await opts.apiAccess.reportError(new Error('Unexpected Discord webhook request body'), body as any);
                return;
            }

            if (body.type === WebhookType.Event && body.event) {
                switch (body.event.type) {
                    case 'APPLICATION_AUTHORIZED':
                        await opts.apiAccess.sendAnalyticsEvents([
                            {
                                user_id: body.event.data.user.id,
                                event_type: 'DiscordApplicationAuthorize',
                                event_properties: {
                                    integrationType: body.event.data.integration_type === 0 ? 'server' : 'user',
                                    serverID: body.event.data.guild?.id,
                                },
                            },
                        ]);
                        break;

                    case 'ENTITLEMENT_CREATE':
                        // Nothing yet
                        break;

                    default:
                        unreachable(body.event);
                }
            }
        },
    };
}
